import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import { useParams } from 'react-router-dom';
import { useClinicDashboard } from '../context/ClinicDashboardContext';
import ViewHeader from '../components/ViewHeader';
import KpiCard from '../components/KpiCard';
import CategoryBar from '../components/CategoryBar';
import DateFilter from '../components/DateFilter';
import EmptyState from '../components/EmptyState';
import api from '../../../../services/api';

const PIE_COLORS = ['#0f4c5c', '#f97316', '#10b981', '#06b6d4', '#d946ef', '#22c55e', '#1e3a5f', '#c2410c'];

const OutpatientCare = ({ onBack }) => {
    const { clinicId } = useParams();
    const { sharedMonthYear } = useClinicDashboard();
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ year: sharedMonthYear.year });
    const [data, setData] = useState({
        kpis: [],
        followUpBars: [],
        outpatientTimeData: [],
        visitByDayData: [],
        shareByType: []
    });

    useEffect(() => {
        const fetchOutpatientData = async () => {
            try {
                setLoading(true);
                const response = await api.get(`/api/v1/clinics/${clinicId}/dashboard/outpatient?year=${filters.year}`);
                if (response.data?.status === 'success') {
                    setData(response.data.data);
                }
            } catch (error) {
                console.error("Error fetching outpatient care data:", error);
            } finally {
                setLoading(false);
            }
        };

        if (clinicId) {
            fetchOutpatientData();
        }
    }, [clinicId, filters]);

    if (loading) {
        return <div className="p-4">Loading outpatient care report...</div>;
    }

    const filterMonthlyChartData = (chartData, year) => {
        const now = new Date();
        if (year < now.getFullYear()) return chartData;
        return chartData.slice(0, now.getMonth() + 1);
    };

    const outpatientTimeData = filterMonthlyChartData(data.outpatientTimeData, filters.year);

    const hasFollowUpBars = data.followUpBars.some(b => b.value > 0);
    const hasShareByType = data.shareByType.some(s => s.value > 0);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', flex: 1, minHeight: 0 }}>
            <ViewHeader
                title="Outpatient Care"
                subtitle={`Year ${filters.year}`}
                onBack={onBack}
                filter={
                    <DateFilter
                        mode="year"
                        value={filters}
                        onChange={setFilters}
                    />
                }
            />

            {/* Main 2-column layout */}
            <div style={{ display: 'flex', gap: '16px', flex: 1, minHeight: 0 }}>
                {/* Left column */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', flex: 1, minHeight: 0 }}>
                    {/* Key metrics — at TOP of left column */}
                    <div className="clinic-dashboard-section-card" style={{ animationDelay: '0ms' }}>
                        <div className="clinic-dashboard-section-title">Key metrics</div>
                        <div className="clinic-dashboard-kpi-row two">
                            {data.kpis.map((kpi, i) => <KpiCard key={i} {...kpi} />)}
                        </div>
                    </div>

                    {/* Status of the follow up */}
                    <div className="clinic-dashboard-section-card" style={{ display: 'flex', flexDirection: 'column', animationDelay: '80ms' }}>
                        <div className="clinic-dashboard-section-title">Status of the follow up</div>
                        {hasFollowUpBars ? (
                            <div className="clinic-dashboard-catbar-list large">
                                {data.followUpBars.map((bar, i) => <CategoryBar key={i} {...bar} />)}
                            </div>
                        ) : (
                            <EmptyState message="No follow-up data for this period" />
                        )}
                    </div>

                    {/* Outpatient visit by Day — bar chart at BOTTOM of left */}
                    <div className="clinic-dashboard-section-card" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', animationDelay: '160ms' }}>
                        <div className="clinic-dashboard-section-title">Outpatient visit by Day</div>
                        <div className="clinic-dashboard-chart-wrap" style={{ minHeight: '180px' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={data.visitByDayData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis
                                        dataKey="name"
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#9ca3af', fontSize: 11 }}
                                    />
                                    <YAxis
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#9ca3af', fontSize: 12 }}
                                    />
                                    <Tooltip
                                        contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                    />
                                    <Bar animationBegin={600} dataKey="visit" fill="#0f4c5c" radius={[4, 4, 0, 0]} name="visit" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>

                {/* Right column */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', flex: 1, minHeight: 0 }}>
                    {/* Outpatient over time — line chart */}
                    <div className="clinic-dashboard-section-card" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', animationDelay: '240ms' }}>
                        <div className="clinic-dashboard-section-title">Outpatient over time</div>
                        <div className="clinic-dashboard-chart-wrap" style={{ minHeight: '200px' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={outpatientTimeData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis
                                        dataKey="name"
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#9ca3af', fontSize: 12 }}
                                    />
                                    <YAxis
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#9ca3af', fontSize: 12 }}
                                    />
                                    <Tooltip
                                        contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                    />
                                    <Line animationBegin={600} type="monotone" dataKey="value" stroke="#0f4c5c" strokeWidth={2} dot={{ r: 4, fill: '#0f4c5c' }} activeDot={{ r: 6 }} name="outpatient" />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Outpatient share by type — pie */}
                    <div className="clinic-dashboard-section-card" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', animationDelay: '320ms' }}>
                        <div className="clinic-dashboard-section-title">Outpatient share by type</div>
                        {hasShareByType ? (
                            <div className="clinic-dashboard-chart-wrap" style={{ minHeight: '200px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                <ResponsiveContainer width="100%" height="70%">
                                    <PieChart>
                                        <Pie
                                            data={data.shareByType}
                                            outerRadius={80}
                                            paddingAngle={1}
                                            dataKey="value"
                                            stroke="#fff"
                                            strokeWidth={1}
                                        >
                                            {data.shareByType.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                                    </PieChart>
                                </ResponsiveContainer>
                                <div className="clinic-dashboard-legend" style={{ flexWrap: 'wrap', justifyContent: 'center', gap: '8px 16px' }}>
                                    {data.shareByType.map((entry, index) => (
                                        <div key={index} className="clinic-dashboard-legend-item">
                                            <span className="clinic-dashboard-legend-dot" style={{ backgroundColor: PIE_COLORS[index] }} />
                                            <span className="clinic-dashboard-legend-label">{entry.name}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <EmptyState message="No type data for this period" />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OutpatientCare;
